import React, { useContext, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Header,
  Icon,
  Link,
  SegmentedControl,
  SpaceBetween,
  Table,
  TextContent,
} from '@amzn/awsui-components-react-v3';

import { LAST_TRANSACTIONS_STORED } from '../../constants';
import useLocalStorage from '../../hooks/useLocalStorage';
import { Context, Types } from '../../store';
import { AsyncState } from '../../store/types';
import { CopyText } from '../CopyText/CopyText';
import { getMobilityUrl } from '../Utils/Utils';

import "./executions.scss"
import _ from 'lodash';

interface ExecutionCached {
  receiveExecutionUUId: string;
  externalScannableId: string;
  receiveScanEndTime: string;
  receiveScanStartTime: string;
  manufacturerPartNumber: string;
  mpnIpnId: string;
  trackingId: string;
  receivedManufacturerPartNumber: string;
  receiveReferenceId: string;
  referenceLineItemId: string;
  internalPartNumber: string;
  description: string;
  status: AsyncState;
  receivedLineItemList: [];
}

const urlParams = new URLSearchParams(window.location.search);
const showInProgressParam = urlParams.get('showInProgress');
const showInprogressVal = showInProgressParam === 'true' || false;

export const ExecutionsTable = ({ executions, inProgress, completed, visibleColumns, draft, closeMenu }: any) => {
  const { dispatch } = useContext(Context);

  const getStatus = (status: AsyncState) => {
    switch (status) {
      case AsyncState.ERROR:
        return (
          <Box margin={{ left: 's' }}>
            <Badge color="red">
              <Icon name="status-negative" /> Failed
            </Badge>
          </Box>
        );
      case AsyncState.OK:
        return (
          <Badge color="green">
            <Icon name="status-positive" /> Received
          </Badge>
        );
      case undefined:
      case AsyncState.CREATED:
        return (
          <Badge color="grey">
            <Icon name="status-pending" /> Draft
          </Badge>
        );
      case AsyncState.IN_PROGRESS:
        return (
          <Badge color="grey">
            <Icon name="status-in-progress" /> In Progress
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleRemoveInprogress = () => {
    dispatch({
      type: Types.removeInProgress,
    });
  };

  return (
    <Table
      header={
        <Header
          actions={
            inProgress && false ? (
              <Button variant="inline-link" onClick={handleRemoveInprogress}>
                <Box color="text-status-error">Remove All</Box>
              </Button>
            ) : null
          }
        />
      }
      columnDefinitions={[
        {
          id: 'mpn',
          header: 'MPN',
          maxWidth: 170,
          cell: (e: ExecutionCached) => {
            const mpnIpnId = e?.mpnIpnId;
            const executionId = e?.receiveExecutionUUId;
            closeMenu && closeMenu();
            const onClick = () => {
              executionId
                ? window.open(
                  `/inventory-receive/receive/shipment/${e.receiveReferenceId}?shipmentId=${e.externalScannableId}&mpnIpnId=${mpnIpnId}&executionId=${executionId}`,
                  '_blank',
                  'rel=noopener noreferrer',
                )
                : window.open(
                  `/inventory-receive/receive/shipment/${e.receiveReferenceId}?mpnIpnId=${mpnIpnId}`,
                  '_blank',
                  'rel=noopener noreferrer',
                );
            };
            return (
              <SpaceBetween size="xxxs" direction="horizontal">
                {e.manufacturerPartNumber && <CopyText label="" value={e.manufacturerPartNumber} />}
                <Link fontSize="heading-m" onFollow={onClick}>
                  {e?.manufacturerPartNumber}
                </Link>
              </SpaceBetween>
            );
          },
        },
        {
          id: 'ipn',
          header: 'IPN',
          maxWidth: 150,
          cell: (e: ExecutionCached) => {
            return (
              <SpaceBetween size="xxxs" direction="horizontal">
                {e?.internalPartNumber && <CopyText label="" value={e.internalPartNumber} />}
                {e?.internalPartNumber}
              </SpaceBetween>
            );
          },
        },
        {
          id: 'shipmentid',
          header: 'Receive ID',
          maxWidth: 170,
          cell: (e: ExecutionCached) => {
            return (
              e?.externalScannableId && (
                <CopyText label="" value={e?.externalScannableId}>
                  {completed ? (
                    <Link
                      target="_blank"
                      href={`${getMobilityUrl()}search_type=all&search_string=${e.externalScannableId
                        }&max_rows=50&query=GO`}
                    >
                      {e?.externalScannableId}
                    </Link>
                  ) : (
                    e?.externalScannableId
                  )}
                </CopyText>
              )
            );
          },
        },
        {
          id: 'status',
          maxWidth: 100,
          header: 'Status',
          cell: (e: ExecutionCached) => getStatus(e?.status),
        },
        {
          id: 'scanStartTime',
          maxWidth: 170,
          header: 'Scan Start Time',
          cell: (e) => {
            return e?.receiveScanStartTime
              ? `${new Date(e?.receiveScanStartTime).toLocaleDateString('en-US')}-
          ${new Date(e?.receiveScanStartTime).toLocaleTimeString()}`
              : null;
          },
        },
        {
          id: 'scanEndTime',
          maxWidth: 170,
          header: draft ? 'Updated Time' : 'Submission Time',
          cell: (e) => {
            const endDate = parseInt(e?.receiveScanEndTime);
            return e?.receiveScanEndTime
              ? `${new Date(endDate).toLocaleDateString('en-US')}-
          ${new Date(endDate).toLocaleTimeString()}`
              : null;
          },
        },
        {
          id: 'executionID',
          header: 'Execution ID',
          width: 150,
          cell: (e: ExecutionCached) => {
            const onClick = () => {
              const ipn = e.internalPartNumber;
              const refereceLineItemId = e?.referenceLineItemId;
              const mpn = e.manufacturerPartNumber;
              const executionId = e.receiveExecutionUUId;
              closeMenu && closeMenu();
              window.open(
                `/inventory-receive/receive/shipment/${e.receiveReferenceId}?shipmentId=${e.externalScannableId}&ipn=${ipn}&refereceLineItemId=${refereceLineItemId}&mpn=${mpn}&executionId=${executionId}`,
                '_blank',
                'rel=noopener noreferrer',
              );
            };
            return (
              <div className="execution-id">
                <SpaceBetween size="xxxs" direction="horizontal">
                  {e.receiveExecutionUUId && <CopyText label="" value={e.receiveExecutionUUId} />}
                  <Link fontSize="heading-m" onFollow={onClick}>
                    <TextContent>{e.receiveExecutionUUId}</TextContent>
                  </Link>
                </SpaceBetween>
              </div>
            );
          },
        },
      ]}
      items={executions}
      loadingText="Loading resources"
      trackBy="name"
      visibleColumns={visibleColumns}
      empty={
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <b>No receives</b>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export const ExecutionsTabbed = ({ closeMenu }) => {
  const { state } = useContext(Context);
  const [showInprogress] = useLocalStorage('show-in-progress', false);
  const shouldShowInprogress = true || showInprogressVal || showInprogress;
  const [selectedId, setSelectedId] = useState('draft');
  const executions = state?.executions || [];
  const executionList: ExecutionCached[] = executions?.map((e: ExecutionCached) => {
    const lineItemReceived = e?.receivedLineItemList?.[0];
    return {
      receiveExecutionUUId: e?.receiveExecutionUUId,
      receiveReferenceId: e?.receiveReferenceId,
      referenceLineItemId: e?.referenceLineItemId,
      externalScannableId: e?.externalScannableId,
      receiveScanStartTime: e?.receiveScanStartTime,
      receiveScanEndTime: e?.receiveScanEndTime,
      mpnIpnId: e?.mpnIpnId,
      trackingId: e?.trackingId,
      manufacturerPartNumber:
        lineItemReceived?.manufacturerPartNumber || lineItemReceived?.receivedManufacturerPartNumber,
      internalPartNumber: lineItemReceived?.internalPartNumber || lineItemReceived?.receivedInternalPartNumber,
      status: e?.status,
    };
  });
  const uniqueExecutions = _.uniqWith(executionList, _.isEqual);
  const sortedExecutions = uniqueExecutions.sort((e1, e2) => e2?.receiveScanEndTime - e1?.receiveScanEndTime);

  const failed = sortedExecutions.filter((e) => e?.status === AsyncState.ERROR);
  const completed = sortedExecutions.filter((e) => e?.status === AsyncState.OK);
  const inProgress = sortedExecutions.filter((e) => e?.status === AsyncState.IN_PROGRESS);
  const drafts = sortedExecutions.filter(
    (e) =>
      !e?.receiveExecutionUUId &&
      (e?.status === AsyncState.CREATED || !e?.status) &&
      !!e?.manufacturerPartNumber &&
      !!e?.internalPartNumber,
  );

  const getExecutions = () => {
    switch (selectedId) {
      case 'all':
        return [...drafts, ...completed, ...failed];
      case 'draft':
        return drafts;
      case 'completed':
        return _.uniqBy(completed,"receiveExecutionUUId");
      case 'inprogress':
        return inProgress;
      case 'failed':
        return failed;
      default:
        return [];
    }
  };

  let DEFAULT_TABS = [
    {
      text: `Drafts (${drafts.length})`,
      iconName: 'status-pending',
      id: 'draft',
    },
    {
      text: `Completed (${completed.length})`,
      iconName: 'status-positive',
      id: 'completed',
    },
    {
      text: `Failed (${failed.length})`,
      iconName: 'status-negative',
      id: 'failed',
    },
  ];

  if (shouldShowInprogress) {
    DEFAULT_TABS = [
      {
        text: `Drafts (${drafts.length})`,
        iconName: 'status-pending',
        id: 'draft',
      },
      {
        text: `In progress (${inProgress.length})`,
        iconName: 'status-in-progress',
        id: 'inprogress',
      },
      {
        text: `Completed (${completed.length})`,
        iconName: 'status-positive',
        id: 'completed',
      },
      {
        text: `Failed (${failed.length})`,
        iconName: 'status-negative',
        id: 'failed',
      },
    ];
  }

  return (
    <div className="executions-tabbed">
      <Box margin={{ bottom: 'm' }}>
        <Box margin={{ bottom: 'm' }}>
          <SpaceBetween direction="horizontal" size="s">
            <Header variant="h3">{`Last ${LAST_TRANSACTIONS_STORED} shipments you tried to receive`}</Header>
          </SpaceBetween>
        </Box>
        <SegmentedControl
          selectedId={selectedId}
          onChange={({ detail }) => setSelectedId(detail.selectedId)}
          label={`Segmented control ${selectedId}`}
          options={DEFAULT_TABS}
        />
      </Box>
      <ExecutionsTable
        executions={getExecutions()}
        visibleColumns={
          selectedId === 'draft'
            ? ['mpn', 'ipn', 'shipmentid', 'status', 'scanEndTime']
            : ['mpn', 'ipn', 'shipmentid', 'status', 'scanEndTime', 'executionID']
        }
        completed={selectedId === 'completed'}
        failed={selectedId === 'failed'}
        draft={selectedId === 'draft'}
        inProgress={selectedId === 'inprogress'}
        closeMenu={closeMenu}
      />
    </div>
  );
};
